import { useCallback } from 'react';
import {
  generateX25519KeyPair,
  generateEd25519KeyPair,
  deriveWrapKey,
  importWrapKey,
  encryptAES,
  exportPublicKey,
  arrayBufferToBase64,
  generateAESKey
} from '../lib/crypto';

export function useCrypto() {
  const generateIdentityKeys = useCallback(async () => {
    const x25519Pair = await generateX25519KeyPair();
    const ed25519Pair = await generateEd25519KeyPair();
    
    const identityPk = arrayBufferToBase64(await exportPublicKey(x25519Pair.publicKey));
    const signingPk = arrayBufferToBase64(await exportPublicKey(ed25519Pair.publicKey));
    
    return {
      x25519KeyPair: x25519Pair,
      ed25519KeyPair: ed25519Pair,
      identityPk,
      signingPk
    };
  }, []);

  const deriveKeyFromPassphrase = useCallback(async (passphrase: string) => {
    const result = await deriveWrapKey(passphrase);
    return {
      wrapKey: result.wrapKey,
      salt: arrayBufferToBase64(result.salt.buffer as ArrayBuffer)
    };
  }, []);

  const encryptPrivateKeyWithPassphrase = useCallback(async (
    privateKey: ArrayBuffer,
    passphrase: string
  ) => {
    const { wrapKey, salt } = await deriveWrapKey(passphrase);
    const wrapKeyObj = await importWrapKey(wrapKey);

    const { ciphertext, nonce } = await encryptAES(wrapKeyObj, privateKey);
    
    return {
      encryptedSk: arrayBufferToBase64(ciphertext),
      nonce: arrayBufferToBase64(nonce.buffer as ArrayBuffer),
      salt: arrayBufferToBase64(salt.buffer as ArrayBuffer)
    };
  }, []);

  const generateFeedKey = useCallback(async () => {
    const key = await generateAESKey();
    const rawKey = await crypto.subtle.exportKey('raw', key) as ArrayBuffer;
    return {
      key,
      rawKey: arrayBufferToBase64(rawKey)
    };
  }, []);

  return {
    generateIdentityKeys,
    deriveKeyFromPassphrase,
    encryptPrivateKeyWithPassphrase,
    generateFeedKey
  };
}
